/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMenuLayout } from 'api/hooks/useMenuLayout'
import { useGetCategories } from 'api/hooks/useGetCategories'
import { useParams, useSearchParams } from 'react-router-dom'

import { SplashScreen } from 'components/menus/splash-screen'
import { useHiddenProducts } from 'api/hooks/useHiddenProducts'
import { HeaderV2 } from 'components/menus/headerV2'
import { Footer } from 'components/menus/footer'
import { getRGBColor } from 'utils'
import { useLocales } from 'api/hooks/useGetLocalesFormSlug'
import { Enum_Menu_Locale } from 'api/graphql/types.generated'
import ReservationButton from 'components/reservationButton'
import { StickyCategoriesV2 } from 'components/menus/sticky-categories-v2'
import { CategoriesV2 } from 'components/menus/categories-v2'
import { getVisibleCategories } from 'utils/categories'

export const Restaurant = () => {
  const [locale, setLocale] = useState(navigator.language)
  const [showLoader, setShowLoader] = useState(true)

  const { data: categories, categoriesIsFetched } = useGetCategories(locale)
  const [searchParams] = useSearchParams()
  const { slug } = useParams<{ slug: string }>()

  const { locales } = useLocales()

  const { layout, layoutIsFetched } = useMenuLayout(locale)
  const { hidden_products } = useHiddenProducts()
  const { i18n } = useTranslation()

  const current = i18n.language

  const handleLocale = (lng: string) => {
    i18n.changeLanguage(lng)
    setLocale(lng)
  }

  // 0. Effect pour l'attribut lang du <html> et le lien canonical
  useEffect(() => {
    document.documentElement.lang = locale.split('-')[0]

    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', `${window.location.origin}/${slug || ''}`)

    let ogUrl = document.querySelector('meta[property="og:url"]')
    if (!ogUrl) {
      ogUrl = document.createElement('meta')
      ogUrl.setAttribute('property', 'og:url')
      document.head.appendChild(ogUrl)
    }
    ogUrl.setAttribute('content', `${window.location.origin}/${slug || ''}`)
  }, [locale, slug])

  // 1. Effect pour le Meta Title & Description (HTML standard)
  useEffect(() => {
    if (layout) {
      const metaTitle = layout.meta_title || 'Big Mamma — Menu'
      document.title = metaTitle

      const ogTitle = document.querySelector('meta[property="og:title"]')
      if (ogTitle) ogTitle.setAttribute('content', metaTitle)

      const twitterTitle = document.querySelector('meta[name="twitter:title"]')
      if (twitterTitle) twitterTitle.setAttribute('content', metaTitle)

      const metaDescText = layout.meta_description || 'Big Mamma menus'

      let metaDesc = document.querySelector('meta[name="description"]')
      if (!metaDesc) {
        metaDesc = document.createElement('meta')
        metaDesc.setAttribute('name', 'description')
        document.head.appendChild(metaDesc)
      }
      metaDesc.setAttribute('content', metaDescText)

      let ogDesc = document.querySelector('meta[property="og:description"]')
      if (!ogDesc) {
        ogDesc = document.createElement('meta')
        ogDesc.setAttribute('property', 'og:description')
        document.head.appendChild(ogDesc)
      }
      ogDesc.setAttribute('content', metaDescText)

      let twitterDesc = document.querySelector('meta[name="twitter:description"]')
      if (!twitterDesc) {
        twitterDesc = document.createElement('meta')
        twitterDesc.setAttribute('name', 'twitter:description')
        document.head.appendChild(twitterDesc)
      }
      twitterDesc.setAttribute('content', metaDescText)
    }
  }, [layout])

  // 2. Effect pour générer et injecter dynamiquement le Schema.org (JSON-LD)
  useEffect(() => {
    if (layout && categories) {
      const publicCategories = getVisibleCategories(categories, hidden_products)

      const menuSections = publicCategories.map((cat: any) => {
        const productsList = cat?.products || []

        return {
          '@type': 'MenuSection',
          name: cat?.category?.attributes?.title || cat?.title || 'Catégorie',
          description: cat?.category?.attributes?.description || cat?.description || undefined,
          hasMenuItem: productsList.map((prod: any) => {
            const attributes = prod?.product?.attributes || prod

            // Correction ESLint ici : utilisation de la Dot Notation pour l'objet nutrition
            const nutritionData: any = {}
            if (attributes?.kcal && attributes?.kcal !== '' && attributes?.kcal !== 'null') {
              nutritionData['@type'] = 'NutritionInformation'
              nutritionData.kcal = `${attributes.kcal}`
            }

            return {
              '@type': 'MenuItem',
              name: attributes?.title || attributes?.name || '',
              description: attributes?.description || '',
              offers: {
                '@type': 'Offer',
                price: attributes?.price ? String(attributes.price) : '0.00',
                priceCurrency: 'EUR'
              },
              ...(Object.keys(nutritionData).length > 0 ? { nutrition: nutritionData } : {})
            }
          })
        }
      })

      const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'Restaurant',
        url: `${window.location.origin}/${slug || layout.slug || ''}`,
        name: layout.restaurant || 'Big Mamma',
        description: layout.description || layout.meta_description || 'Big Mamma menus',
        servesCuisine: ['Italian cuisine, Pizzeria'],
        hasMenu: {
          '@type': 'Menu',
          inLanguage: locale.split('-')[0],
          hasMenuSection: menuSections
        }
      }

      let scriptElement = document.getElementById('schema-restaurant-jsonld') as HTMLScriptElement

      if (!scriptElement) {
        scriptElement = document.createElement('script')
        scriptElement.id = 'schema-restaurant-jsonld'
        scriptElement.type = 'application/ld+json'
        document.head.appendChild(scriptElement)
      }

      scriptElement.text = JSON.stringify(schemaData)
    }

    return () => {
      const scriptElement = document.getElementById('schema-restaurant-jsonld')
      if (scriptElement) {
        scriptElement.remove()
      }
    }
  }, [layout, categories, slug, locale, hidden_products])

  // 3. Reste de tes effets existants
  useEffect(() => {
    const browserLang = navigator.language.split('-')[0] as Enum_Menu_Locale

    if (locales && locales.includes(browserLang)) {
      const htmlElement = document.documentElement
      htmlElement.setAttribute('translate', 'no')
    }

    if (current !== locale) i18n.changeLanguage(locale)
  }, [locale, i18n, current, locales])

  useEffect(() => {
    if (!layout && layoutIsFetched) {
      setLocale('en')
    }
  }, [layoutIsFetched, layout])

  useEffect(() => {
    if (!searchParams.get('categorie')) {
      const element = document.querySelector('#header')
      if (element) {
        element.scrollIntoView(true)
      }
    }
  }, [searchParams])

  useEffect(() => {
    if (layout) {
      async function loadTitleFonts() {
        const font = new FontFace(
          'titleFont',
          `url(${layout?.title_font?.data?.attributes?.font_file?.data?.attributes?.url})`
        )
        await font.load()
        document.fonts.add(font)
        document.body.classList.add('titleFont-loaded')
        const url = layout?.title_font?.data?.attributes?.font_file?.data?.attributes?.url
        if (url?.includes('Cardo') || url?.includes('cardo')) {
          document.body.classList.add('noBestOfImg')
        }
      }
      async function loadBodyFonts() {
        const font = new FontFace(
          'bodyFont',
          `url(${layout?.body_font?.data?.attributes?.font_file?.data?.attributes?.url})`
        )
        await font.load()
        document.fonts.add(font)
        document.body.classList.add('bodyFont-loaded')
        const url = layout?.body_font?.data?.attributes?.font_file?.data?.attributes?.url
        if (url?.includes('Cardo') || url?.includes('cardo')) {
          document.body.classList.add('noBestOfImg')
        }
      }
      async function loadDescriptionFonts() {
        const font = new FontFace(
          'descriptionFont',
          `url(${layout?.description_font?.data?.attributes?.font_file?.data?.attributes?.url})`
        )
        await font.load()
        document.fonts.add(font)
        document.body.classList.add('bodyFont-loaded')
        const url = layout?.description_font?.data?.attributes?.font_file?.data?.attributes?.url
        if (url?.includes('Cardo') || url?.includes('cardo')) {
          document.body.classList.add('noBestOfImg')
        }
      }
      loadTitleFonts()
      loadDescriptionFonts()
      loadBodyFonts()
    }
  }, [layout])

  useEffect(() => {
    // Suppression du console.log pour corriger l'avertissement 'no-console' d'ESLint
    const timer = setTimeout(() => {
      setShowLoader(false)
    }, 1000)
    return () => clearTimeout(timer)
  }, [categoriesIsFetched])

  if (!layout) {
    return <></>
  }

  const background_color = layout.background_color ?? '#ffffff'
  const font_color = layout.font_color ?? '#000000'
  const secondary_color = layout.secondary_color ?? '#ffffff  '

  const primary = getRGBColor(font_color, 'primary')
  const secondary = getRGBColor(background_color, 'secondary')
  const tertiary = getRGBColor(secondary_color, 'tertiary')

  const isArabic = locale.startsWith('ar') || locale === 'ar'
  const publicCategories = getVisibleCategories(categories, hidden_products)

  if (showLoader && layout) {
    return (
      <SplashScreen
        restaurantAdress={layout.restaurant_adress}
        color={font_color}
        restaurantName={layout.restaurant}
        imgUrl={layout?.logo?.data?.attributes?.url}
      />
    )
  }

  // Suppression du console.log final ici aussi pour valider le build final
  return (
    <>
      <style>:root {`{${primary} ${secondary} ${tertiary}}`}</style>
      <div id="main" className="h-screen overflow-x-hidden font-mammaBody">
        <div className="container flex justify-center w-full max-w-[768px] mx-auto">
          <div className="w-full ">
            <div className="sticky top-0 z-50">
              {/* HEADER */}
              <HeaderV2
                setLocale={(lng) => handleLocale(lng)}
                wifi={layout.wifi_code}
                showCaloriesBtn={layout.show_calories_button}
                showAllergensBtn={layout.show_allergen_button}
                description={layout.description}
                isLoading={!layoutIsFetched}
                sub_description={layout.sub_description}
                restaurantName={layout.restaurant}
                imgUrl={layout?.logo?.data?.attributes?.url}
              />

              {/* STICKY CATEGORIES */}
              {categoriesIsFetched && <StickyCategoriesV2 isArabic={isArabic} categories={publicCategories} />}
            </div>

            <h1 className="font-mammaBody text-primary mt-6 text-center text-[14px] font-bold tracking-[0.02em] leading-[120%]">
              {layout.titre_h1}
            </h1>

            {layout.description && (
              <div className="mb-[26px] mt-6 font-mammaBody text-primary px-4">
                <p className="font-mammaDescription whitespace-pre-wrap text-center text-[14px] leading-[120%]">
                  {layout.description}
                </p>
                <p className="mt-3 text-center text-[14px] font-bold tracking-[0.02em] leading-[120%]">
                  OPEN WIFI: {layout.wifi_code}
                </p>
                {layout?.cover?.data?.attributes?.url && (
                  <img
                    className="max-h-[76px] mx-auto mt-6 max-w-full"
                    src={layout?.cover?.data?.attributes?.url}
                    alt={layout.restaurant || 'restaurant logo'}
                  />
                )}
              </div>
            )}

            {/* CATEGORIES */}
            {showLoader ? (
              <div className="h-auto">
                <SplashScreen
                  restaurantAdress={layout.restaurant_adress}
                  restaurantName={layout.restaurant}
                  color={font_color}
                  imgUrl={layout?.logo?.data?.attributes?.url}
                />
              </div>
            ) : (
              <CategoriesV2 categories={publicCategories} hidden_products={hidden_products} isArabic={isArabic} />
            )}

            <Footer
              image={layout.our_surprise?.data?.attributes?.url}
              surpriseLink={layout.our_surprise_link}
              allergens={layout.footer_allergens}
              teams={layout.footer_team}
            />
          </div>
        </div>
      </div>
      <ReservationButton reservationUrl={layout.reservation_url} label={layout.reservation_button_label} />
    </>
  )
}

export default Restaurant
